import React, { useState, useCallback, createContext, useContext, ReactNode } from 'react';
import { appInsights } from '../services/appInsights';
import { clientConfig } from '../utilities/configService';

interface IAPIErrorProviderProps {
    children: ReactNode;
}

interface IAPIErrorContextType {
    apiError: IAPIError | null,
    addError: (message: string, status: string) => void,
    trackError: (message: string, status: string) => void,
    clearError: () => void
}

interface IAPIError {
    message: string,
    status: string
}


const APIErrorContext = createContext<IAPIErrorContextType | undefined>(undefined);

export function useAPIErrorContext() {
    const context = useContext(APIErrorContext);
    if (context === undefined) {
        throw new Error('useAPIErrorContext must be used within a APIErrorProvider');
    }
    return context;
}


export default function APIErrorProvider({ children }: IAPIErrorProviderProps) {
    const [apiError, setAPIError] = useState<IAPIError | null>(null);
    const applicationName = clientConfig?.APPLICATION_NAME || '';

    const clearError = () => {
        setAPIError(null);
    };

    const addError = (message: string, status: string) => {
        setAPIError({ message, status });
        var exceptionMessage = status ? `${message}: ${status}` : message;
        appInsights.trackException({ exception: new Error(exceptionMessage), severityLevel: 3, properties: { 'applicationName':applicationName } });
    };

    const trackError = (message: string, status: string) => {
        clearError();  //tracking should only log, it should not show a banner
        var exceptionMessage = status ? `${message}: ${status}` : message;
        appInsights.trackException({ exception: new Error(exceptionMessage), severityLevel: 3, properties: { 'applicationName': applicationName } });
    };

    const contextValue: IAPIErrorContextType = {
        apiError,
        addError: useCallback((message: string, status: string) => { addError(message, status) }, []),
        trackError: useCallback((message: string, status: string) => { trackError(message, status) }, []),
        clearError: useCallback(() => clearError(), [])
    };

    return (
        <APIErrorContext.Provider value={contextValue}>
            {children}
        </APIErrorContext.Provider>
    );
}